import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../../app/services/hooks/useOrganizationService';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';
import useRefreshAuth from '../../../../hooks/useRefreshAuth';
import DefaultCard from '../../../../components/default/DefaultCard';
import ButtonGeneral from '../../../../components/buttons/ButtonGeneral';
import PlanCard from '../../components/PlanCard';
import { LawyerPlan, PlanSummary, formatDate, isSafePaymentUrl, statusLabel } from '../../types';
import { OrganizationContextProps } from '../index';

const SubscriptionTab: React.FC<OrganizationContextProps> = ({ organization, membership, reload }) => {
    const {
        getOrganizationPlans, createSubscription, completeSubscription, cancelSubscription,
        fetchingPlans, fetchingSubscription
    } = useOrganizationService();
    const { requestConfirmation } = useReactConfirmAlert();
    const { refreshAuth } = useRefreshAuth();

    const [plans, setPlans] = useState<LawyerPlan[]>([]);
    const [current, setCurrent] = useState<PlanSummary | null>(null);
    const [choosing, setChoosing] = useState<number | null>(null);

    const canManage = membership.is_admin && organization.status === 'active';

    const load = () => {
        getOrganizationPlans({
            onSuccess: (response) => {
                setPlans(response.data.plans);
                setCurrent(response.data.current);
            },
            onError: (response) => toast.error(response.message)
        });
    };

    useEffect(() => {
        load();
    }, []);


    const choosePlan = (plan: LawyerPlan) => {
        requestConfirmation({
            title: `¿Cambiar al plan ${plan.name}?`,
            message: plan.is_free
                ? 'Tu estudio pasará al plan gratuito de inmediato.'
                : `Se cobrarán ${plan.price_with_tax.toLocaleString('es-CL')} CLP (IVA incluido) cada mes con Flow. Si ya tienes un plan pagado, se reemplaza.`,
            buttons: {
                confirmButton: {
                    label: 'Sí, continuar',
                    onClick: () => {
                        setChoosing(plan.id);
                        createSubscription(plan.id, {
                            onSuccess: (response) => {
                                setChoosing(null);
                                if (response.data.needs_card_registration) {
                                    if (!isSafePaymentUrl(response.data.registration_url)) {
                                        toast.error('No se pudo abrir el registro de tarjeta. Intenta nuevamente.');
                                        return;
                                    }
                                    toast.info(response.message);
                                    window.location.href = response.data.registration_url;
                                    return;
                                }
                                toast.success(response.message);
                                setCurrent(response.data.current);
                                reload();
                                refreshAuth();
                            },
                            onError: (response) => {
                                setChoosing(null);
                                toast.error(response.message);
                            }
                        });
                    }
                },
                cancelButton: { label: 'Cancelar' }
            }
        });
    };

    const handleComplete = () => {
        completeSubscription({
            onSuccess: (response) => {
                toast.success(response.message);
                setCurrent(response.data.current);
                reload();
                refreshAuth();
            },
            onError: (response) => toast.error(response.message)
        });
    };

    const handleCancel = () => {
        requestConfirmation({
            title: '¿Cancelar la suscripción?',
            message: 'Se deja de cobrar al final del período. Tu plan sigue vigente hasta esa fecha y luego vuelves al plan gratuito: no pierdes tus datos.',
            buttons: {
                confirmButton: {
                    label: 'Sí, cancelar',
                    onClick: () =>
                        cancelSubscription({
                            onSuccess: (response) => {
                                toast.success(response.message);
                                setCurrent(response.data.current);
                                reload();
                            },
                            onError: (response) => toast.error(response.message)
                        })
                },
                cancelButton: { label: 'Volver' }
            }
        });
    };

    const subscription = current?.subscription;
    const activePlanId = current && !current.is_fallback ? current.plan?.id : null;

    return (
        <>
            <DefaultCard>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                    <div>
                        <h5 className="mb-1">Plan actual: {current?.plan?.name || '—'}</h5>
                        <p className="text-muted small mb-0">
                            {current?.is_fallback
                                ? 'Sin suscripción pagada vigente: aplican los límites del plan gratuito.'
                                : subscription?.is_manual_assignment
                                    ? `Asignado por Monere${subscription.end_date ? ` hasta el ${formatDate(subscription.end_date)}` : ''}.`
                                    : subscription?.is_subscription
                                        ? `Cobro mensual con Flow · estado: ${subscription.status || 'pendiente'}${subscription.next_invoice_date ? ` · próximo cobro ${formatDate(subscription.next_invoice_date)}` : ''}.`
                                        : 'Plan gratuito.'}
                        </p>
                    </div>
                    {canManage && subscription?.is_subscription && subscription.status !== 'cancelled' && (
                        <ButtonGeneral onClick={handleCancel} title="Cancelar suscripción" variant="outline" size="small"
                                       loading={fetchingSubscription} icon="fa-ban" />
                    )}
                </div>
                {current?.pending_subscription && canManage && (
                    <div className="alert alert-warning small mt-3 mb-0 d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <span>
                            Tienes pendiente el plan <strong>{current.pending_subscription.plan_name}</strong>: falta registrar la tarjeta en Flow.
                        </span>
                        <span className="d-flex gap-2">
                            {current.pending_subscription.flow_url && (
                                <a className="btn btn-sm btn-warning" href={current.pending_subscription.flow_url}>Ir a Flow</a>
                            )}
                            <ButtonGeneral onClick={handleComplete} title="Ya registré mi tarjeta" size="small" loading={fetchingSubscription} />
                        </span>
                    </div>
                )}
            </DefaultCard>

            <h5 className="mt-4 mb-3">
                Planes para {organization.organization_type === 'firm' ? 'estudios' : 'abogados independientes'}
            </h5>
            {fetchingPlans && plans.length === 0 ? (
                <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
            ) : (
                <div className="row" data-testid="plans-grid">
                    {plans.map((plan) => (
                        <div className="col-md-6 col-xl-4 mb-3" key={plan.id}>
                            <PlanCard
                                plan={plan}
                                isCurrent={activePlanId === plan.id}
                                actionLabel={canManage ? (activePlanId === plan.id ? 'Plan actual' : 'Elegir plan') : undefined}
                                actionDisabled={activePlanId === plan.id || !canManage}
                                loading={choosing === plan.id}
                                onAction={() => choosePlan(plan)}
                            />
                        </div>
                    ))}
                    {plans.length === 0 && <p className="text-muted">Aún no hay planes publicados para tu tipo de cuenta.</p>}
                </div>
            )}
            {!membership.is_admin && <p className="text-muted small">Solo el administrador del estudio puede cambiar el plan.</p>}
        </>
    );
};

export default SubscriptionTab;
